import { useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

interface RequestTracker {
  count: number;
  lastReset: number;
  warnings: number;
}

const RATE_LIMIT_WINDOW = 60000; // 1 minute
const WARNING_THRESHOLD = 10; // 10 requests per minute
const ERROR_THRESHOLD = 20; // 20 requests per minute

export function useRequestMonitor() {
  const { toast } = useToast();
  const trackerRef = useRef<RequestTracker>({
    count: 0,
    lastReset: Date.now(),
    warnings: 0
  });

  const trackRequest = useCallback(() => {
    const now = Date.now();
    const tracker = trackerRef.current;

    // Reset counter if window has passed
    if (now - tracker.lastReset > RATE_LIMIT_WINDOW) {
      tracker.count = 0;
      tracker.lastReset = now;
      tracker.warnings = 0;
    }

    tracker.count++;

    // Show warning if approaching rate limit
    if (tracker.count === WARNING_THRESHOLD && tracker.warnings === 0) {
      tracker.warnings++;
      toast({
        title: "Cảnh báo",
        description: "Đang gửi nhiều yêu cầu. Vui lòng chậm lại để tránh giới hạn.",
        variant: "default"
      });
    }

    // Show error if exceeding rate limit
    if (tracker.count >= ERROR_THRESHOLD) {
      toast({
        title: "Lỗi",
        description: "Quá nhiều yêu cầu. Vui lòng đợi một chút trước khi thử lại.",
        variant: "destructive"
      });
      return false; // Prevent request
    }

    return true; // Allow request
  }, [toast]);

  const getRequestCount = useCallback(() => {
    const now = Date.now();
    const tracker = trackerRef.current;

    if (now - tracker.lastReset > RATE_LIMIT_WINDOW) {
      return 0;
    }

    return tracker.count;
  }, []);

  return { trackRequest, getRequestCount };
}